import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Erro ao carregar posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="blog-page">
      <SEO 
        title="Blog sobre Tours Virtuais 360° | Gran Tour 360"
        description="Artigos, tendências e novidades sobre tours virtuais 360°, tecnologia 3DVista e estratégias de marketing visual para negócios."
        url="https://grantour360.com.br/blog"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Blog</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Insights e Novidades
            </h1>
            <p className="text-gray-500 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              Estratégias, tendências e conhecimento sobre tours virtuais 360° e marketing visual para negócios.
            </p>
          </motion.div>
        </div>

        {loading ? (
          <div className="text-center py-24">
            <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-[#FF3366] rounded-full animate-spin"></div>
            <p className="text-gray-500 mt-4">Carregando posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-gray-500 text-lg">Nenhum post disponível no momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl hover:border-[#FF3366]/30 transition-all group"
                data-testid={`blog-post-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(post.published_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {post.author}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">{post.title}</h2>
                  <p className="text-gray-500 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center gap-2 text-[#FF3366] font-semibold text-sm hover:gap-3 transition-all"
                    data-testid={`read-more-${index}`}
                  >
                    Ler mais
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
